/* =============================================================================
   MASKHY — order tracking (sections/order-tracking.liquid)
   Looks a tracking number up on NextSmartShip's public tracking endpoint (the same one their
   own tracking site uses; it allows the shop's origin) and draws it in the shop's own style:
   status, three-step progress, carrier details and the full history, newest first.
   /pages/track?n=NUMBER (also ?s= or ?tracking=) looks the number up straight away, so the
   shipping email can link here.
   ============================================================================= */
(function () {
  'use strict';

  const root = document.querySelector('[data-track]');
  if (!root) return;

  const endpoint = root.dataset.endpoint;
  const contactUrl = root.dataset.contact || '/pages/contact';
  const form = root.querySelector('[data-track-form]');
  const input = root.querySelector('[data-track-input]');
  const send = root.querySelector('[data-track-send]');
  const msg = root.querySelector('[data-track-msg]');
  const result = root.querySelector('[data-track-result]');
  const lang = document.documentElement.lang || 'en';

  const STEPS = ['Order confirmed', 'On its way', 'Delivered'];
  const STATUS = {
    TRACK_STATUS_RECEIVED: { step: 0, title: 'Order confirmed', line: 'We have your order and it is being prepared.' },
    TRACK_STATUS_IN_TRANSIT: { step: 1, title: 'On its way', line: 'Your order has left the warehouse and is travelling to you.' },
    TRACK_STATUS_DELIVERED: { step: 2, title: 'Delivered', line: 'Your order has been delivered. Enjoy the ride.' },
    TRACK_STATUS_EXCEPTION: { step: 1, title: 'Needs attention', line: 'The carrier reported a problem with this delivery.', alert: true },
  };
  // The warehouse's own first events, in the shop's words. Carrier events are shown as written.
  const EVENT_COPY = [
    [/^order is being processed\.?$/i, 'Order received, being prepared'],
    [/^order submit+ed\.?$/i, 'Order sent to the warehouse'],
    [/^order is shipped out\.?$/i, 'Shipped'],
  ];

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const clean = (v) => String(v || '').trim().replace(/^[#']/, '').replace(/[\s-]+/g, '').toUpperCase();
  const valid = (v) => /^[A-Z0-9]{6,40}$/.test(v);

  const fmtDate = (iso, withTime) => {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const opts = withTime
      ? { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { day: 'numeric', month: 'long', year: 'numeric' };
    try { return new Intl.DateTimeFormat(lang, opts).format(d); } catch (e) { return d.toLocaleString(); }
  };
  const country = (code) => {
    if (!code) return '';
    try { return new Intl.DisplayNames([lang], { type: 'region' }).of(code) || code; } catch (e) { return code; }
  };
  const eventText = (content) => {
    let text = String(content || '').trim();
    let place = '';
    const m = text.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (m) { place = m[1]; text = m[2]; }
    for (const [re, copy] of EVENT_COPY) if (re.test(text)) { text = copy; break; }
    return { place, text };
  };

  const say = (text, kind) => {
    msg.textContent = text || '';
    msg.dataset.kind = kind || '';
  };

  function render(t) {
    const d = t.track_detail;
    const st = STATUS[d.status] || STATUS.TRACK_STATUS_IN_TRANSIT;
    const events = (d.items || [])
      .filter((e) => e && e.content)
      .reverse() // same-second events keep their order, newest first
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    const latest = events[0];
    const latestText = latest ? eventText(latest.content) : null;

    const facts = [['Tracking number', esc(t.tracking_number)]];
    if (t.destination_country) facts.push(['Destination', esc(country(t.destination_country))]);
    if (t.estimated_delivery_date) facts.push(['Estimated delivery', esc(fmtDate(t.estimated_delivery_date, false))]);
    if (t.last_mile_service_provider) {
      const site = /^https?:\/\//.test(t.last_mile_website || '') ? t.last_mile_website : '';
      facts.push(['Local carrier', site ? `<a href="${esc(site)}" target="_blank" rel="noopener">${esc(t.last_mile_service_provider)}</a>` : esc(t.last_mile_service_provider)]);
    }
    if (t.last_mile_tracking_number) {
      const url = /^https?:\/\//.test(t.last_mile_tracking_url || '') ? t.last_mile_tracking_url : '';
      facts.push(['Carrier tracking', url
        ? `<a href="${esc(url)}" target="_blank" rel="noopener">${esc(t.last_mile_tracking_number)} <span aria-hidden="true">↗</span></a>`
        : esc(t.last_mile_tracking_number)]);
    }
    const phones = (t.last_mile_phone || []).map((p) => String(p).replace(/^[^:]*:\s*/, '').trim()).filter(Boolean);
    if (phones.length) facts.push(['Carrier phone', phones.map((p) => `<a href="tel:${esc(p.replace(/[^\d+]/g, ''))}">${esc(p)}</a>`).join('<br>')]);

    const steps = STEPS.map((label, i) => {
      const state = i < st.step || (i === st.step && d.status === 'TRACK_STATUS_DELIVERED') ? 'done' : i === st.step ? 'now' : 'next';
      return `<li class="trk__step is-${state}${st.alert && i === st.step ? ' is-alert' : ''}"${state === 'now' ? ' aria-current="step"' : ''}><i aria-hidden="true"></i><span>${esc(label)}</span></li>`;
    }).join('');

    const history = events.map((e, i) => {
      const { place, text } = eventText(e.content);
      return `<li class="trk__event${i === 0 ? ' is-latest' : ''}"><time datetime="${esc(e.timestamp)}">${esc(fmtDate(e.timestamp, true))}</time><p>${esc(text)}${place ? `<span class="trk__place">${esc(place)}</span>` : ''}</p></li>`;
    }).join('');

    result.innerHTML = `
      <div class="trk${st.alert ? ' trk--alert' : ''}">
        <div class="trk__head">
          <p class="kicker">Status</p>
          <h2 class="trk__status">${esc(st.title)}</h2>
          <p class="trk__line">${esc(st.line)}</p>
          ${latestText ? `<p class="trk__latest"><span>Latest</span> ${esc(latestText.text)}${latestText.place ? ` · ${esc(latestText.place)}` : ''} · ${esc(fmtDate(latest.timestamp, true))}</p>` : ''}
        </div>
        <ol class="trk__steps" style="--step: ${st.step}" role="list">${steps}</ol>
        <div class="trk__body">
          <dl class="trk__facts">${facts.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${v}</dd></div>`).join('')}</dl>
          <div class="trk__history">
            <h3 class="trk__h3">History</h3>
            ${history ? `<ol class="trk__events" role="list">${history}</ol>` : '<p class="trk__empty">No movements yet.</p>'}
          </div>
        </div>
        <p class="trk__contact">${st.alert ? 'We\'ll help you sort it out.' : 'Something not right?'} <a href="${esc(contactUrl)}">Contact us</a></p>
      </div>`;
    result.hidden = false;
  }

  let busy = false;
  async function lookUp(raw, scroll) {
    const number = clean(raw);
    if (!valid(number)) {
      say('Check the number: letters and digits only, as in your shipping email.', 'error');
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }
    if (busy) return;
    busy = true;
    input.value = number;
    input.removeAttribute('aria-invalid');
    send.classList.add('is-busy');
    say('Looking it up…');

    const url = new URL(location.href);
    url.searchParams.delete('s');
    url.searchParams.delete('tracking');
    url.searchParams.set('n', number);
    history.replaceState(null, '', url);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-NSS-Lang': 'en' },
        body: JSON.stringify({ search: [number] }),
      });
      const json = await res.json();
      if (!res.ok || json.code !== 0) throw new Error(json.message || res.status);
      const t = ((json.data && json.data.list) || [])[0];
      if (!t || !t.track_detail) {
        result.hidden = true;
        result.innerHTML = '';
        say('We can\'t find this number yet. Tracking can take a little while to appear after your shipping email — check the number or try again later.', 'error');
        return;
      }
      say('');
      render(t);
      if (scroll) result.scrollIntoView({ behavior: document.documentElement.classList.contains('motion-ok') ? 'smooth' : 'auto', block: 'start' });
    } catch (e) {
      say('Tracking is unavailable right now. Please try again in a few minutes.', 'error');
    } finally {
      busy = false;
      send.classList.remove('is-busy');
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    lookUp(input.value, true);
  });

  const params = new URLSearchParams(location.search);
  const initial = params.get('n') || params.get('s') || params.get('tracking');
  if (initial) {
    input.value = initial;
    lookUp(initial, false);
  }
})();
